import React, { useEffect } from "react";
import { Star, Gift, Globe, Layout, Plug } from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";

const JustEatTakeaway = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletsListLeft = (
    <BulletList>
      <BulletItem>Support multiple countries and rollout phases</BulletItem>
      <BulletItem>
        Integrate deeply with core ordering, customer, and offer systems
      </BulletItem>
      <BulletItem>Handle points accrual and redemption securely</BulletItem>
      <BulletItem>
        Give non-technical teams control over offers and campaigns
      </BulletItem>
      <BulletItem>Remain extensible for future loyalty mechanics</BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Globally Scalable Loyalty Platform"
        subtitle="Designing and delivering a flexible, points-based loyalty system across multiple European markets"
        category="ENGINEERING"
        role="Principle Full Stack Software Engineer"
        year="2019 & 2020"
        platform="Web & Mobile"
        technologies={[
          "AWS",
          " Node.js",
          " React.js",
          " Next.js",
          " Serverless Framework",
        ]}
        client="JustEatTakeaway / Thuisbezorgd"
      />
      <VisualShowcase
        imageUrl={images.jet2}
        imageAlt="Thuisbezorgd delivery"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "Scaling loyalty across countries, systems, and teams",
          "JET’s existing loyalty tooling was limited in flexibility and difficult to scale across markets. As the programme grew, the business needed a platform that could:",
        ]}
        solutionContent={[
          "A scalable, integrated loyalty platform with a rebuilt CMS",
          "The solution was a robust, secure loyalty platform designed to integrate seamlessly with JET’s existing core systems while remaining flexible enough to evolve over time.",
          "Alongside the customer-facing experience, we completely rebuilt the internal CMS used by the loyalty team. This empowered non-technical users to manage campaigns, offers, and content more effectively, improving visibility and speed of iteration.",
          "The platform was designed with international rollout in mind, supporting phased launches across European markets, starting with France.",
        ]}
        bulletsLeft={bulletsListLeft}
        extraChallengeContent={[
          "This wasn’t just a UI problem — it was a core platform challenge, sitting at the intersection of product, engineering, operations, and marketing.",
        ]}
        metrics={[
          {
            value: "Multi-country",
            label: "Loyalty platform rollout across European markets",
          },
          {
            value: "Points-based",
            label: "Earn and redeem mechanics integrated with ordering",
          },
          {
            value: "Rebuilt",
            label: "CMS empowering loyalty teams to manage offers",
          },
          {
            value: "Core",
            label: "Deep integration with JET’s existing systems",
          },
        ]}
      />
      <VisualShowcase
        title="Customer Points Dashboard"
        description="The user-friendly points dashboard, allowing customers to easily track and redeem loyalty points for rewards."
        layout="full"
        imageUrl={images.thuisbezorgd3}
        imageAlt="Customer points dashboard"
      />
      <VisualShowcase
        title="Defining the Loyalty Program Requirements"
        description="Collaborating with the marketing team to align on customer needs and define the foundational elements of the loyalty platform."
        layout="full"
        backgroundColor="black"
        descriptionColor="#9ca3af"
        titleColor="#ffffff"
        imageUrl={images.thuisbezorgd4}
        imageAlt="Discussing requirements with the marketing team"
      />
      <VisualShowcase
        title="Notification Card Design Concepts"
        description="A set of notification card designs to welcome users to the loyalty platform and highlight special offers."
        layout="full"
        imageUrl={images.thuisbezorgd6}
        imageAlt="Palette of notification card designs"
      />
      <KeyFeatures
        title="A Loyalty Platform Built for Scale"
        description="Features designed to support growth, flexibility, and long-term evolution."
        features={[
          {
            icon: Star,
            title: "Points-based loyalty mechanics",
            description:
              "Customers earn points through everyday ordering, creating a clear and rewarding loyalty loop.",
          },
          {
            icon: Gift,
            title: "Offer discovery and redemption",
            description:
              "Seamless browsing and redemption of rewards directly within the ordering experience.",
          },
          {
            icon: Globe,
            title: "Multi-market scalability",
            description:
              "Designed to support phased rollouts across countries with varying business needs.",
          },
          {
            icon: Layout,
            title: "Rebuilt loyalty CMS",
            description:
              "Empowered internal teams to manage offers, campaigns, and content without engineering support.",
          },
          {
            icon: Plug,
            title: "Deep system integration",
            description:
              "Integrated tightly with JET’s core ordering and customer systems for reliability and consistency.",
          },
        ]}
      />
      <ProcessSection
        title="Building a Loyalty Platform at Global Scale"
        description="A collaborative, agile approach focused on integration, scalability, and long-term product vision."
        phases={[
          {
            number: "01",
            title: "Product Vision & Alignment",
            description:
              "Worked with product and stakeholders to define a long-term loyalty vision aligned with customer retention and business goals.",
            deliverables: [
              "Product vision alignment",
              "Loyalty mechanics definition",
              "Stakeholder workshops",
              "Market rollout planning",
            ],
          },
          {
            number: "02",
            title: "Platform & Integration Design",
            description:
              "Designed loyalty flows that integrated cleanly with existing ordering, customer, and offer systems.",
            deliverables: [
              "System integration mapping",
              "Data and points flow definition",
              "CMS capability design",
              "Scalability considerations",
            ],
          },
          {
            number: "03",
            title: "Implementation & Delivery",
            description:
              "Built and iterated on the loyalty platform within an embedded Scrum team, delivering features incrementally.",
            deliverables: [
              "Customer-facing loyalty features",
              "CMS rebuild and enhancements",
              "Sprint-based delivery",
              "Cross-team collaboration",
            ],
          },
          {
            number: "04",
            title: "Rollout & Market Expansion",
            description:
              "Supported phased rollout across European markets, starting with France, while preparing the platform for future expansion.",
            deliverables: [
              "Country-specific rollout support",
              "Monitoring and issue resolution",
              "Iteration based on real usage",
              "Foundation for future markets",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactDescription="The project delivered a scalable loyalty platform that strengthened customer engagement while empowering internal teams to operate independently. By integrating deeply with core systems and rebuilding the CMS, the solution balanced customer experience, operational efficiency, and long-term extensibility."
        impactLabel="Project Impact"
        impactTitle="A future-proof loyalty foundation for a global marketplace"
        nextProject={{
          title: "Codam Professional Development Workshop",
          link: "/project/codam-professional-development",
        }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "Scalable platforms succeed when teams are empowered",
          description:
            "This project reinforced that building scalable systems isn’t only about architecture — it’s about enabling the people who operate them. By investing in internal tooling alongside customer-facing features, the loyalty platform became easier to run, adapt, and grow across markets.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { JustEatTakeaway };

/*

Project Context

Just Eat Takeaway.com (JET) is one of the world’s leading online food delivery marketplaces, connecting millions of consumers with local restaurant partners globally. Formed in 2020 through the merger of Just Eat and Takeaway.com, the company operates at massive scale across multiple countries, brands, and platforms.

As part of its customer retention strategy, Takeaway.com operates a points-based loyalty programme that allows customers to earn points when ordering food, which can then be redeemed for rewards and offers.


My Role

Senior Software Engineer

I worked as part of an embedded agency team within Just Eat Takeaway.com, collaborating closely with JET engineers, designers, and a dedicated product owner.

Responsibilities included:

Building customer-facing loyalty features

Integrating loyalty functionality with existing core systems

Contributing to platform scalability and extensibility

Collaborating daily within a Scrum-based delivery team

Supporting international rollout planning and execution
*/
